import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Brain, Loader2, CheckCircle2, X, Users, Sparkles, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const SCOPE_LABEL = {
  tenant_candidate: 'Candidate',
  tenant_approved: 'Approved',
  user: 'Personal',
};
const SCOPE_STYLE = {
  tenant_candidate: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
  tenant_approved: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
  user: 'bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-300',
};

export default function LearningRecords() {
  const canWrite = useCan('measures:certify');
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState({ candidate: 0, approved: 0 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');

  useEffect(() => {
    api.getSemanticModels().then((r) => {
      const ms = r.models || r || [];
      setModels(ms);
      if (ms.length && !modelId) setModelId(ms[0].model_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const r = await api.getLearningRecords(modelId);
      setRecords(r.learning_records || []);
      setCounts({ candidate: r.candidate_count || 0, approved: r.approved_count || 0 });
    } catch (e) {
      toast.error(e.message || 'Failed to load learning records');
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => { load(); }, [load]);

  const promote = async (rec) => {
    setBusyId(rec.learning_id);
    try {
      await api.promoteLearningRecord(rec.learning_id);
      toast.success('Promoted — AI Explore will use this workspace-wide');
      load();
    } catch (e) { toast.error(e.message || 'Could not promote'); }
    finally { setBusyId(''); }
  };

  const dismiss = async (rec) => {
    setBusyId(rec.learning_id);
    try {
      await api.rejectLearningRecord(rec.learning_id);
      toast.success('Dismissed');
      load();
    } catch (e) { toast.error(e.message || 'Could not dismiss'); }
    finally { setBusyId(''); }
  };

  const planSummary = (p) => {
    const bits = [];
    if ((p?.measures || []).length) bits.push((p.measures).map((m) => String(m).replace(/_/g, ' ')).join(', '));
    if ((p?.dimensions || []).length) bits.push(`by ${(p.dimensions).map((d) => String(d).replace(/_/g, ' ')).join(', ')}`);
    return bits.join(' ');
  };

  return (
    <div data-testid="learning-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Label className="text-sm text-muted-foreground">Model</Label>
        <select
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          className="h-9 rounded-md border border-border/50 bg-background px-3 text-sm"
          data-testid="learning-model-select"
        >
          {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
        </select>
        <span className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="learning-counts">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" /> {counts.candidate} candidate{counts.candidate === 1 ? '' : 's'}
          <span className="mx-1">·</span>
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {counts.approved} approved
        </span>
      </div>

      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        Patterns AI Explore learned from accepted answers and accepted corrections. Promote a candidate to teach
        it to the whole workspace, or dismiss it. Approved patterns become golden tests.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : records.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="learning-empty">
          Nothing learned yet for this model. Accept an answer (👍) or accept a correction in AI Explore and it shows up here.
        </div>
      ) : (
        <div className="space-y-3" data-testid="learning-list">
          {records.map((r) => (
            <div key={r.learning_id}
              className="bg-card/60 border border-border/40 rounded-2xl p-4"
              data-testid={`learning-card-${r.learning_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] ${SCOPE_STYLE[r.scope] || ''}`}
                      data-testid={`learning-scope-${r.learning_id}`}>
                      {SCOPE_LABEL[r.scope] || r.scope}
                    </Badge>
                    {r.is_golden_test && (
                      <Badge variant="outline" className="text-[10px] bg-violet-500/10 border-violet-500/30 text-violet-700 dark:text-violet-300">
                        <FlaskConical className="w-3 h-3 mr-1" /> Golden test
                      </Badge>
                    )}
                    {r.scope === 'tenant_candidate' && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Users className="w-3 h-3" /> {r.contributor_count || 1} contributor{(r.contributor_count || 1) === 1 ? '' : 's'}
                        {r.strong_candidate && <span className="ml-1 text-amber-600 font-medium">· ready to promote</span>}
                      </span>
                    )}
                  </div>
                  <div className="font-heading font-medium text-foreground text-sm flex items-center gap-1.5">
                    <Brain className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="truncate">{r.question}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{planSummary(r.plan)}</p>
                </div>
                {canWrite && r.scope === 'tenant_candidate' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => dismiss(r)} disabled={busyId === r.learning_id}
                      data-testid={`dismiss-learning-${r.learning_id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Dismiss
                    </Button>
                    <Button size="sm" onClick={() => promote(r)} disabled={busyId === r.learning_id}
                      data-testid={`promote-learning-${r.learning_id}`}>
                      {busyId === r.learning_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                      Promote
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { LearningRecords };
